import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const ChildProfileContext = createContext(null);
const STORAGE_KEY = "wmleye:childProfileId";

export function ChildProfileProvider({ children }) {
  const { status } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [selectedId, setSelectedId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch (_) {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/profiles");
      setProfiles(data.items || []);
      if (selectedId && !(data.items || []).find((p) => p.id === selectedId)) {
        setSelectedId(null);
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch (_) {}
      }
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    if (status === "authed") refresh();
    else if (status === "guest") setProfiles([]);
  }, [status, refresh]);

  const select = useCallback((profileId) => {
    setSelectedId(profileId);
    try {
      if (profileId) localStorage.setItem(STORAGE_KEY, profileId);
      else localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
  }, []);

  const selected = profiles.find((p) => p.id === selectedId) || null;

  return (
    <ChildProfileContext.Provider
      value={{ profiles, selected, selectedId, select, loading, refresh }}
    >
      {children}
    </ChildProfileContext.Provider>
  );
}

export function useChildProfile() {
  const ctx = useContext(ChildProfileContext);
  if (!ctx) throw new Error("useChildProfile must be used within ChildProfileProvider");
  return ctx;
}
